/**
 * Serialization utilities for Three.js objects and application state
 */

import * as THREE from 'three';

/**
 * Convert Three.js BufferGeometry to OBJ format string
 */
export function geometryToOBJ(geometry) {
  if (!geometry || !geometry.attributes.position) {
    throw new Error('Invalid geometry: missing position attribute');
  }

  const positions = geometry.attributes.position.array;
  const normals = geometry.attributes.normal?.array;
  const uvs = geometry.attributes.uv?.array;
  const indices = geometry.index?.array;

  let objContent = '# OBJ file generated by Addiwise\n';
  objContent += `# Vertices: ${positions.length / 3}\n`;
  objContent += `# Generated: ${new Date().toISOString()}\n\n`;

  // Write vertices
  for (let i = 0; i < positions.length; i += 3) {
    objContent += `v ${positions[i]} ${positions[i + 1]} ${positions[i + 2]}\n`;
  }

  // Write normals if available
  if (normals) {
    objContent += '\n';
    for (let i = 0; i < normals.length; i += 3) {
      objContent += `vn ${normals[i]} ${normals[i + 1]} ${normals[i + 2]}\n`;
    }
  }

  // Write texture coordinates if available
  if (uvs) {
    objContent += '\n';
    for (let i = 0; i < uvs.length; i += 2) {
      objContent += `vt ${uvs[i]} ${uvs[i + 1]}\n`;
    }
  }

  // Write faces
  objContent += '\n';
  if (indices) {
    for (let i = 0; i < indices.length; i += 3) {
      const i1 = indices[i] + 1;
      const i2 = indices[i + 1] + 1;
      const i3 = indices[i + 2] + 1;

      if (normals && uvs) {
        objContent += `f ${i1}/${i1}/${i1} ${i2}/${i2}/${i2} ${i3}/${i3}/${i3}\n`;
      } else if (normals) {
        objContent += `f ${i1}//${i1} ${i2}//${i2} ${i3}//${i3}\n`;
      } else if (uvs) {
        objContent += `f ${i1}/${i1} ${i2}/${i2} ${i3}/${i3}\n`;
      } else {
        objContent += `f ${i1} ${i2} ${i3}\n`;
      }
    }
  } else {
    // Non-indexed geometry
    const vertexCount = positions.length / 3;
    for (let i = 0; i < vertexCount; i += 3) {
      const i1 = i + 1;
      const i2 = i + 2;
      const i3 = i + 3;

      if (normals && uvs) {
        objContent += `f ${i1}/${i1}/${i1} ${i2}/${i2}/${i2} ${i3}/${i3}/${i3}\n`;
      } else if (normals) {
        objContent += `f ${i1}//${i1} ${i2}//${i2} ${i3}//${i3}\n`;
      } else if (uvs) {
        objContent += `f ${i1}/${i1} ${i2}/${i2} ${i3}/${i3}\n`;
      } else {
        objContent += `f ${i1} ${i2} ${i3}\n`;
      }
    }
  }

  return objContent;
}

/**
 * Parse OBJ format string to Three.js BufferGeometry
 */
export function objToGeometry(objContent) {
  const vertices = [];
  const normals = [];
  const uvs = [];
  const faces = [];

  const lines = objContent.split('\n');

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;

    const parts = trimmed.split(/\s+/);
    const type = parts[0];

    switch (type) {
      case 'v':
        vertices.push(
          parseFloat(parts[1]),
          parseFloat(parts[2]),
          parseFloat(parts[3])
        );
        break;

      case 'vn':
        normals.push(
          parseFloat(parts[1]),
          parseFloat(parts[2]),
          parseFloat(parts[3])
        );
        break;

      case 'vt':
        uvs.push(
          parseFloat(parts[1]),
          parseFloat(parts[2])
        );
        break;

      case 'f':
        // Parse face indices (1-based to 0-based)
        const faceVertices = [];
        for (let i = 1; i < parts.length; i++) {
          const indices = parts[i].split('/');
          faceVertices.push(parseInt(indices[0]) - 1);
        }

        // Triangulate if needed
        if (faceVertices.length === 3) {
          faces.push(...faceVertices);
        } else if (faceVertices.length === 4) {
          // Quad to two triangles
          faces.push(
            faceVertices[0], faceVertices[1], faceVertices[2],
            faceVertices[0], faceVertices[2], faceVertices[3]
          );
        }
        break;
    }
  }

  // Create BufferGeometry
  const geometry = new THREE.BufferGeometry();

  if (vertices.length > 0) {
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
  }

  if (normals.length > 0) {
    geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  }

  if (uvs.length > 0) {
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  }

  if (faces.length > 0) {
    geometry.setIndex(faces);
  }

  // Compute missing attributes
  if (normals.length === 0) {
    geometry.computeVertexNormals();
  }

  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();

  return geometry;
}

/**
 * Serialize Three.js Material to JSON
 */
export function serializeMaterial(material) {
  if (!material) return null;

  const serialized = {
    type: material.type,
    uuid: material.uuid,
    name: material.name,
    userData: material.userData
  };

  // Common material properties - ensure we capture all relevant properties
  if (material.color) {
    serialized.color = material.color.getHex();
    serialized.colorR = material.color.r;
    serialized.colorG = material.color.g;
    serialized.colorB = material.color.b;
  }
  if (material.opacity !== undefined) serialized.opacity = material.opacity;
  if (material.transparent !== undefined) serialized.transparent = material.transparent;
  if (material.visible !== undefined) serialized.visible = material.visible;
  if (material.side !== undefined) serialized.side = material.side;
  if (material.wireframe !== undefined) serialized.wireframe = material.wireframe;
  if (material.vertexColors !== undefined) serialized.vertexColors = material.vertexColors; // Preserve vertex colors flag

  // Material-specific properties
  switch (material.type) {
    case 'MeshBasicMaterial':
      if (material.wireframe !== undefined) serialized.wireframe = material.wireframe;
      break;

    case 'MeshLambertMaterial':
    case 'MeshPhongMaterial':
      if (material.emissive) {
        serialized.emissive = material.emissive.getHex();
        serialized.emissiveR = material.emissive.r;
        serialized.emissiveG = material.emissive.g;
        serialized.emissiveB = material.emissive.b;
      }
      break;

    case 'MeshStandardMaterial':
      if (material.metalness !== undefined) serialized.metalness = material.metalness;
      if (material.roughness !== undefined) serialized.roughness = material.roughness;
      if (material.emissive) {
        serialized.emissive = material.emissive.getHex();
        serialized.emissiveR = material.emissive.r;
        serialized.emissiveG = material.emissive.g;
        serialized.emissiveB = material.emissive.b;
      }
      break;
  }

  return serialized;
}

/**
 * Deserialize JSON to Three.js Material
 */
export function deserializeMaterial(data) {
  if (!data || !data.type) {
    console.warn('Invalid material data for deserialization:', data);
    return null;
  }

  console.log('Deserializing material:', data.type, 'saved color:', data.color);

  let material;

  switch (data.type) {
    case 'MeshBasicMaterial':
      material = new THREE.MeshBasicMaterial();
      break;
    case 'MeshLambertMaterial':
      material = new THREE.MeshLambertMaterial();
      break;
    case 'MeshPhongMaterial':
      material = new THREE.MeshPhongMaterial();
      break;
    case 'MeshStandardMaterial':
      material = new THREE.MeshStandardMaterial();
      break;
    default:
      material = new THREE.MeshLambertMaterial(); // Default to Lambert
      break;
  }

  // Restore color with multiple fallback methods
  if (typeof data.color === 'number') {
    material.color.setHex(data.color);
    console.log('Restored color from hex:', data.color);
  } else if (data.colorR !== undefined && data.colorG !== undefined && data.colorB !== undefined) {
    material.color.setRGB(data.colorR, data.colorG, data.colorB);
    console.log('Restored color from RGB:', data.colorR, data.colorG, data.colorB);
  }

  // Restore other properties with validation
  if (typeof data.opacity === 'number') material.opacity = data.opacity;
  if (typeof data.transparent === 'boolean') material.transparent = data.transparent;
  if (typeof data.visible === 'boolean') material.visible = data.visible;
  if (typeof data.side === 'number') material.side = data.side;
  if (typeof data.wireframe === 'boolean') material.wireframe = data.wireframe;
  if (typeof data.vertexColors === 'boolean') material.vertexColors = data.vertexColors; // Restore vertex colors flag
  if (typeof data.metalness === 'number') material.metalness = data.metalness;
  if (typeof data.roughness === 'number') material.roughness = data.roughness;

  // Restore emissive with fallback
  if (typeof data.emissive === 'number') {
    material.emissive.setHex(data.emissive);
  } else if (data.emissiveR !== undefined && data.emissiveG !== undefined && data.emissiveB !== undefined) {
    material.emissive.setRGB(data.emissiveR, data.emissiveG, data.emissiveB);
  }

  // Ensure proper double-side rendering for prosthetics
  material.side = THREE.DoubleSide;

  if (data.uuid) material.uuid = data.uuid;
  if (data.name) material.name = data.name;
  if (data.userData) material.userData = data.userData;

  console.log('Final material color:', material.color.getHex());

  return material;
}

/**
 * Serialize Zustand store state with custom replacer for circular references
 */
export function serializeStoreState(state, storeOptions = {}) {
  const { excludeKeys = [], includeMetadata = true } = storeOptions;

  const replacer = (key, value) => {
    // Skip excluded keys
    if (excludeKeys.includes(key)) {
      return undefined;
    }

    // Handle Three.js objects
    if (value && value.isObject3D) {
      return {
        __type: 'Object3D',
        type: value.type,
        uuid: value.uuid,
        name: value.name,
        position: value.position.toArray(),
        rotation: value.rotation.toArray(),
        scale: value.scale.toArray(),
        visible: value.visible
      };
    }

    if (value && value.isBufferGeometry) {
      return {
        __type: 'BufferGeometry',
        uuid: value.uuid,
        type: value.type,
        // Note: Actual geometry data should be saved as separate OBJ file
        __objFile: `${value.uuid}.obj`
      };
    }

    if (value && value.isMaterial) {
      return {
        __type: 'Material',
        ...serializeMaterial(value)
      };
    }

    // Handle functions (commands)
    if (typeof value === 'function') {
      return {
        __type: 'Function',
        name: value.name || 'anonymous'
      };
    }

    return value;
  };

  const serialized = JSON.parse(JSON.stringify(state, replacer));

  if (includeMetadata) {
    serialized.__metadata = {
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
  }

  return serialized;
}

/**
 * Deserialize store state with custom reviver
 */
export function deserializeStoreState(serializedState, context = {}) {
  const { geometryCache = {}, materialCache = {} } = context;

  const reviver = (key, value) => {
    if (value && typeof value === 'object' && value.__type) {
      switch (value.__type) {
        case 'Object3D':
          // Create a placeholder object, actual restoration depends on context
          return {
            __isPlaceholder: true,
            __originalData: value
          };

        case 'BufferGeometry':
          // Return placeholder, geometry should be loaded from OBJ file
          return {
            __isPlaceholder: true,
            __originalData: value
          };

        case 'Material':
          return deserializeMaterial(value);

        case 'Function':
          // Functions cannot be serialized, return placeholder
          return {
            __isPlaceholder: true,
            __originalData: value
          };
      }
    }

    return value;
  };

  return JSON.parse(JSON.stringify(serializedState), reviver);
}

/**
 * Create a serializable version of a command object
 */
export function serializeCommand(command, draftId) {
  if (!command) return null;

  return {
    __type: 'Command',
    __metadata: {
      draftId,
      timestamp: new Date().toISOString(),
      commandType: command.constructor?.name || 'Unknown'
    },
    // Note: Actual command data should be reconstructed during deserialization
    // based on the current state and tool configuration
    __placeholder: true
  };
}

/**
 * Compress OBJ data by reducing precision and removing unnecessary data
 */
export function compressOBJData(objContent) {
  const lines = objContent.split('\n');
  const compressedLines = [];

  for (const line of lines) {
    const trimmed = line.trim();

    // Skip comments except the first few
    if (trimmed.startsWith('#') && compressedLines.length > 3) {
      continue;
    }

    // Compress vertex data by reducing precision
    if (trimmed.startsWith('v ')) {
      const parts = trimmed.split(/\s+/);
      if (parts.length >= 4) {
        const x = parseFloat(parts[1]).toFixed(4);
        const y = parseFloat(parts[2]).toFixed(4);
        const z = parseFloat(parts[3]).toFixed(4);
        compressedLines.push(`v ${x} ${y} ${z}`);
        continue;
      }
    }

    // Compress normal data
    if (trimmed.startsWith('vn ')) {
      const parts = trimmed.split(/\s+/);
      if (parts.length >= 4) {
        const x = parseFloat(parts[1]).toFixed(3);
        const y = parseFloat(parts[2]).toFixed(3);
        const z = parseFloat(parts[3]).toFixed(3);
        compressedLines.push(`vn ${x} ${y} ${z}`);
        continue;
      }
    }

    // Keep other lines as-is
    if (trimmed) {
      compressedLines.push(trimmed);
    }
  }

  return compressedLines.join('\n');
}

/**
 * Create a simplified geometry representation for storage
 */
export function geometryToCompactData(geometry) {
  if (!geometry || !geometry.attributes.position) {
    throw new Error('Invalid geometry: missing position attribute');
  }

  const positions = geometry.attributes.position.array;
  const indices = geometry.index?.array;
  const colors = geometry.attributes.color?.array; // Get vertex colors if they exist
  const normals = geometry.attributes.normal?.array; // Also save normals if available
  const uvs = geometry.attributes.uv?.array; // And UVs

  // Store essential data including vertex colors
  const compactData = {
    positions: Array.from(positions).map(v => Math.round(v * 10000) / 10000), // Reduce precision
    indices: indices ? Array.from(indices) : null,
    vertexCount: positions.length / 3
  };

  // Include vertex colors if they exist (this is crucial for STL with vertex colors)
  if (colors) {
    compactData.colors = Array.from(colors).map(c => Math.round(c * 1000) / 1000); // Preserve color precision
    console.log('Saving vertex colors, count:', colors.length / 3);
  }

  // Include normals if available
  if (normals) {
    compactData.normals = Array.from(normals).map(n => Math.round(n * 1000) / 1000);
  }

  // Include UVs if available
  if (uvs) {
    compactData.uvs = Array.from(uvs).map(uv => Math.round(uv * 1000) / 1000);
  }

  return JSON.stringify(compactData);
}

/**
 * Restore geometry from compact data
 */
export function compactDataToGeometry(compactDataStr) {
  const data = JSON.parse(compactDataStr);

  const geometry = new THREE.BufferGeometry();

  if (data.positions) {
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(data.positions, 3));
  }

  if (data.indices) {
    geometry.setIndex(data.indices);
  }

  // Restore vertex colors if they exist (crucial for preserving scan colors)
  if (data.colors) {
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(data.colors, 3));
    console.log('Restored vertex colors, count:', data.colors.length / 3);
  }

  // Restore normals if they exist
  if (data.normals) {
    geometry.setAttribute('normal', new THREE.Float32BufferAttribute(data.normals, 3));
  }

  // Restore UVs if they exist
  if (data.uvs) {
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(data.uvs, 2));
  }

  // Only compute normals if they weren't saved
  if (!data.normals) {
    geometry.computeVertexNormals();
  }

  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();

  return geometry;
}